import { useEffect, useState, type FormEvent } from 'react';
import {
  TASK_PRIORITY_LABEL,
  TASK_PRIORITIES,
  TASK_REPEAT_LABEL,
  TASK_REPEATS,
  TITLE_MAX,
  type CompletionPolicy,
  type CreateTaskRequest,
  type ListSummary,
  type TaskPriority,
  type TaskRepeat,
  type TaskSummary,
  type ValidationDetails,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { taskApi } from '../api/task.api';
import { listApi } from '../api/list.api';
import { AssigneePicker } from './AssigneePicker';
import { DepartmentSelect } from './DepartmentSelect';
import { ProjectSelect } from './ProjectSelect';

/**
 * TE-01 - create a task and hand it to someone.
 *
 * One request does both. Assignees are written in the same transaction as the
 * task, so there is no window where a published task exists with nobody on it.
 */

interface TaskFormProps {
  onCreated: (task: TaskSummary) => void;
  onCancel: () => void;
}

const POLICY_HELP: Record<CompletionPolicy, string> = {
  ANY: 'Anyone assigned can mark it complete',
  ALL: 'Everyone assigned must complete their part',
  LEAD_ONLY: 'Only the lead can mark it complete',
};

/**
 * Today as a local YYYY-MM-DD string - the same shape <input type="date">
 * uses, so it can be compared to dueAt with plain string comparison and
 * passed straight through as the input's min.
 */
function todayLocalDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function TaskForm({ onCreated, onCancel }: TaskFormProps) {
  const { user } = useAuth();
  /** Delegation is admin-only; everyone else creates unassigned tasks. */
  const canAssign =
    user?.role.systemKey === 'ADMIN' || user?.role.systemKey === 'SUPER_ADMIN';
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [dueAt, setDueAt] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [repeat, setRepeat] = useState<TaskRepeat>('NONE');
  const [policy, setPolicy] = useState<CompletionPolicy>('ANY');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [leadId, setLeadId] = useState<string | undefined>(undefined);
  /** Employees do not choose a department - their own is preset (and the
   *  dropdown replaced by a read-only line). Admins pick freely. */
  const [departmentId, setDepartmentId] = useState<string | undefined>(
    () => (canAssign ? undefined : user?.department?.id),
  );
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  /** Kept alongside the id so the title can be saved as "Project - Title". */
  const [projectName, setProjectName] = useState<string | undefined>(undefined);
  /** Personal lists to file the new task under, ticked at creation time
   *  instead of added one-by-one afterwards from the task panel. */
  const [myLists, setMyLists] = useState<ListSummary[] | null>(null);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  /** Inline "no lists yet" affordance - create one without leaving the form. */
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [listBusy, setListBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    void listApi.all().then(setMyLists).catch(() => setMyLists([]));
  }, []);

  function toggleList(listId: string) {
    setSelectedListIds((ids) =>
      ids.includes(listId) ? ids.filter((id) => id !== listId) : [...ids, listId]);
  }

  /** Creates the list, then files this task under it automatically - the
   *  reason to make a list from inside task creation is almost always to put
   *  THIS task somewhere, not to leave it empty. */
  async function createList() {
    const name = newListName.trim();
    if (!name) return;
    setListBusy(true);
    setListError(null);
    try {
      const before = new Set((myLists ?? []).map((l) => l.id));
      const updated = await listApi.create(name);
      setMyLists(updated);
      const created = updated.find((l) => !before.has(l.id));
      if (created) setSelectedListIds((ids) => [...ids, created.id]);
      setNewListName('');
      setCreatingList(false);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'Could not create the list');
    } finally {
      setListBusy(false);
    }
  }

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setErrors({});

    /**
     * Non-admins have no assignee picker - the form promises "this task
     * will be yours alone", so make that true: they are the assignee.
     * Without this, the assigneeIds check below fails with an error that
     * renders inside the hidden picker - i.e. invisibly.
     */
    const effectiveAssigneeIds =
      canAssign ? assigneeIds : user ? [user.id] : [];

    if (!title.trim()) {
      setErrors({ title: 'Give the task a title' });
      return;
    }
    if (policy === 'LEAD_ONLY' && !leadId) {
      setErrors({ leadId: 'Choose a lead, or change how it completes' });
      return;
    }
    if (!dueAt) {
      setErrors({ dueAt: 'A due date is required' });
      return;
    }
    if (dueAt < todayLocalDate()) {
      setErrors({ dueAt: "Due date can't be in the past" });
      return;
    }
    if (!departmentId) {
      setErrors({ departmentId: 'Choose a department' });
      return;
    }
    if (!projectId) {
      setErrors({ projectId: 'Choose a project' });
      return;
    }
    if (effectiveAssigneeIds.length === 0) {
      setErrors({ assigneeIds: 'Assign the task to at least one person - you can pick yourself' });
      return;
    }
    if (remindAt && new Date(remindAt).getTime() <= Date.now()) {
      setErrors({ remindAt: 'Pick a reminder time in the future' });
      return;
    }

    setSubmitting(true);

    /**
     * A project tag also brands the title: picking "To Do" and typing
     * "Login screen" saves the task as "To Do - Login screen". Skipped when
     * the person already typed the prefix themselves.
     */
    const cleanTitle = title.trim();
    const finalTitle =
      projectName && !cleanTitle.toLowerCase().startsWith(`${projectName.toLowerCase()} -`)
        ? `${projectName} - ${cleanTitle}`
        : cleanTitle;

    const body: CreateTaskRequest = {
      title: finalTitle,
      ...(details.trim() ? { details: details.trim() } : {}),
      priority,
      /**
       * <input type="datetime-local"> gives local wall-clock with no zone.
       * new Date() reads it in the browser's zone and toISOString converts to
       * UTC, which is what the DATETIME(3) column stores. Sending the raw
       * string would be off by the timezone offset.
       */
      /**
       * Due is a DATE: the task is due "that day", so it is stored as the
       * END of that local day (23:59:59) - due today all day, overdue only
       * once the day has actually passed.
       */
      ...(dueAt ? { dueAt: new Date(`${dueAt}T23:59:59`).toISOString() } : {}),
      /** Same local -> UTC conversion as dueAt. */
      ...(remindAt ? { remindAt: new Date(remindAt).toISOString() } : {}),
      ...(repeat !== 'NONE' ? { repeat } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(projectId ? { projectId } : {}),
      completionPolicy: policy,
      assigneeIds: effectiveAssigneeIds,
      ...(leadId ? { leadId } : {}),
      // Every task publishes and notifies its assignees the moment it is
      // created - there is no draft state to opt into any more.
    };

    try {
      const task = await taskApi.create(body);
      if (selectedListIds.length > 0) {
        // Best-effort: the task itself is already created, so a list-filing
        // hiccup should not read as "task creation failed".
        await Promise.all(
          selectedListIds.map((id) => listApi.addTask(id, task.id).catch(() => undefined)),
        );
      }
      onCreated(task);
    } catch (error) {
      applyError(error);
      setSubmitting(false);
    }
  }

  function applyError(error: unknown) {
    if (!(error instanceof ApiError)) {
      setFormError('Something went wrong. Please try again.');
      return;
    }

    if (error.code === 'VALIDATION_ERROR' && error.details) {
      const details = error.details as ValidationDetails;
      const mapped: Record<string, string> = {};
      for (const [key, messages] of Object.entries(details)) {
        mapped[key.replace(/^body\./, '')] = messages[0] ?? 'Invalid value';
      }
      setErrors(mapped);
      return;
    }

    setFormError(error.message);
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="flex-1 space-y-5 px-6 py-5">
        {formError && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">
            {formError}
          </p>
        )}

        {/* Project and Department lead the form: WHERE the work belongs is
            chosen first, and the project then prefixes the title below. */}
        <div className="grid grid-cols-2 gap-4">
          <ProjectSelect
            required
            value={projectId}
            onChange={(id, name) => { setProjectId(id); setProjectName(id ? name : undefined); }}
            error={errors.projectId}
            disabled={submitting}
          />
          {canAssign || !user?.department ? (
            <DepartmentSelect
              required
              value={departmentId}
              onChange={setDepartmentId}
              error={errors.departmentId}
              disabled={submitting}
            />
          ) : (
            <div>
              <span className="block text-sm font-medium text-slate-700">Department</span>
              <p className="mt-1.5 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
                {user.department.name}
              </p>
              <p className="mt-1 text-xs text-slate-500">Your own department is used automatically</p>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="task-title" className="block text-sm font-medium text-slate-700">
           Task Title
          </label>
          <div className="mt-1.5 flex">
            {projectName && (
              <span className="inline-flex items-center whitespace-nowrap rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 px-3 text-sm font-medium text-slate-500">
                {projectName} -
              </span>
            )}
            <input
              id="task-title"
              value={title}
              autoFocus
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Service the site generator"
              aria-invalid={errors.title ? true : undefined}
              className={[
                'w-full border px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:ring-2',
                projectName ? 'rounded-r-lg' : 'rounded-lg',
                errors.title
                  ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                  : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
              ].join(' ')}
            />
          </div>
          {errors.title ? (
            <p className="mt-1 text-xs text-red-600">{errors.title}</p>
          ) : projectName && title.trim() ? (
            <p className="mt-1 text-xs text-slate-500">Will be saved as "{projectName} - {title.trim()}"</p>
          ) : null}
        </div>

        <div>
          <label htmlFor="task-details" className="block text-sm font-medium text-slate-700">
            Details <span className="ml-1 text-slate-400">(optional)</span>
          </label>
          <textarea
            id="task-details"
            rows={3}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Anything the person needs to know before starting"
            className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="block text-sm font-medium text-slate-700">Priority</span>
            <div className="mt-1.5 flex gap-1.5">
              {TASK_PRIORITIES.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPriority(value)}
                  className={[
                    'flex-1 rounded-lg px-2 py-1.5 text-xs font-medium ring-1 transition',
                    priority === value
                      ? 'bg-slate-900 text-white ring-slate-900'
                      : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {TASK_PRIORITY_LABEL[value]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="task-due" className="block text-sm font-medium text-slate-700">
              Due <span className="ml-1 text-red-500">*</span>
            </label>
            <input
              id="task-due"
              type="date"
              value={dueAt}
              min={todayLocalDate()}
              onChange={(e) => setDueAt(e.target.value)}
              aria-invalid={errors.dueAt ? true : undefined}
              className={[
                'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2',
                errors.dueAt
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
              ].join(' ')}
            />
            {errors.dueAt && <p className="mt-1 text-xs text-red-600">{errors.dueAt}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="task-remind" className="block text-sm font-medium text-slate-700">
              Remind on <span className="ml-1 text-slate-400">(optional)</span>
            </label>
            <input
              id="task-remind"
              type="datetime-local"
              value={remindAt}
              onChange={(e) => setRemindAt(e.target.value)}
              aria-invalid={errors.remindAt ? true : undefined}
              className={[
                'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2',
                errors.remindAt
                  ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                  : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
              ].join(' ')}
            />
            {errors.remindAt ? (
              <p className="mt-1 text-xs text-red-600">{errors.remindAt}</p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Assignees get a notification at this exact time
              </p>
            )}
          </div>

          <div>
            <label htmlFor="task-repeat" className="block text-sm font-medium text-slate-700">
              Repeat
            </label>
            <select
              id="task-repeat"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value as TaskRepeat)}
              className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              {TASK_REPEATS.map((value) => (
                <option key={value} value={value}>
                  {TASK_REPEAT_LABEL[value]}
                </option>
              ))}
            </select>
            {repeat !== 'NONE' && (
              <p className="mt-1 text-xs text-slate-500">
                After completion it comes back as Not started, due {repeat === 'DAILY' ? 'a day' : repeat === 'WEEKLY' ? 'a week' : 'a month'} later
              </p>
            )}
          </div>
        </div>

        {myLists !== null && (
          <div>
            <span className="block text-sm font-medium text-slate-700">
              Lists <span className="ml-1 text-slate-400">(optional)</span>
            </span>

            {/* NOT a nested <form>: this whole block sits inside the task
                form, and a nested form's submit (or Enter in its input)
                bubbles into the outer one - which used to fire the WHOLE
                task submission and wipe everything typed. Plain elements
                with explicit handlers keep list creation fully inline. */}
            {creatingList ? (
              <div className="mt-1.5 flex gap-1.5">
                <input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // Stop the outer task form's implicit submission.
                      e.preventDefault();
                      void createList();
                    } else if (e.key === 'Escape') {
                      setCreatingList(false); setNewListName(''); setListError(null);
                    }
                  }}
                  placeholder="List name"
                  maxLength={120}
                  autoFocus
                  disabled={listBusy}
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                />
                <button type="button" disabled={listBusy || !newListName.trim()}
                  onClick={() => void createList()}
                  className="rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
                  {listBusy ? 'Creating...' : 'Create'}
                </button>
                <button type="button" disabled={listBusy}
                  onClick={() => { setCreatingList(false); setNewListName(''); setListError(null); }}
                  className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {myLists.map((list) => {
                  const active = selectedListIds.includes(list.id);
                  return (
                    <button
                      key={list.id}
                      type="button"
                      disabled={submitting}
                      onClick={() => toggleList(list.id)}
                      className={[
                        'rounded-full px-2.5 py-1 text-xs font-semibold ring-1 transition disabled:opacity-50',
                        active
                          ? 'bg-brand-600 text-white ring-brand-600 hover:bg-brand-700'
                          : 'bg-white text-slate-500 ring-slate-300 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      {active ? '✓ ' : '+ '}{list.name}
                    </button>
                  );
                })}
                {myLists.length === 0 && (
                  <p className="text-xs text-slate-500">You have not made any lists yet.</p>
                )}
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setCreatingList(true)}
                  className="rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs font-semibold text-brand-700 transition hover:bg-brand-50 disabled:opacity-50"
                >
                  {myLists.length === 0 ? '+ Create list' : '+ New list'}
                </button>
              </div>
            )}

            {listError && <p className="mt-1 text-xs text-red-600">{listError}</p>}
          </div>
        )}

        {canAssign ? (
          <AssigneePicker
            required
            departmentId={departmentId}
            departmentRequired
            selected={assigneeIds}
            leadId={leadId}
            requireLead={policy === 'LEAD_ONLY'}
            onChange={(ids, lead) => { setAssigneeIds(ids); setLeadId(lead); }}
            error={errors.assigneeIds ?? errors.leadId}
            disabled={submitting}
          />
        ) : (
          <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500 ring-1 ring-slate-200">
            This task will be yours alone - assigning tasks to other people is
            done by an administrator.
          </p>
        )}

        {assigneeIds.length > 1 && (
          <div>
            <label htmlFor="task-policy" className="block text-sm font-medium text-slate-700">
              Completes when
            </label>
            <select
              id="task-policy"
              value={policy}
              onChange={(e) => setPolicy(e.target.value as CompletionPolicy)}
              className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              <option value="ANY">Anyone finishes</option>
              <option value="ALL">Everyone finishes</option>
              <option value="LEAD_ONLY">Only the lead</option>
            </select>
            <p className="mt-1 text-xs text-slate-500">{POLICY_HELP[policy]}</p>
          </div>
        )}

      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Creating...' : 'Create and assign'}
        </button>
      </div>
    </form>
  );
}
